import React, { useCallback, useEffect, useState } from 'react';
import AppModal from '../../../components/AppModal/AppModal';
import { api } from '../../../app/http-common';
import NewsletterOrderList from './NewsletterOrderList';

/**
 * Create newsletter — PRD §4.
 *
 * Three stages in one modal so the story selection and its order survive the
 * whole flow (SEL-03):
 *   cap    — only when more stories were selected than a newsletter allows
 *   order  — optional drag-to-reorder (ORD-01)
 *   setup  — first use only, when the bucket has no configuration (CFG-02)
 */
export default function CreateNewsletterModal({
  bucketId,
  bucketName,
  targetLanguage,
  selection,          // [{ bucketStoryId, title }] in display order, already capped
  overflowCount,      // how many selected stories were dropped by the cap, 0 if none
  maxStories,
  onCancel,
  onCreated,
}) {
  const [stage, setStage] = useState(overflowCount > 0 ? 'cap' : 'loading');
  const [items, setItems] = useState(selection);
  const [manualOrderLocked, setManualOrderLocked] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // First-use setup state
  const [description, setDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [nameOptions, setNameOptions] = useState([]);
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');

  const loadConfig = useCallback(async () => {
    try {
      await api.getBucketNewsletterConfig(bucketId);
      setStage('order');
    } catch (err) {
      // 404 is the documented "no configuration yet" signal, not a failure.
      if (err?.response?.status === 404) setStage('describe');
      else setError('Could not check this bucket’s newsletter settings.');
    }
  }, [bucketId]);

  useEffect(() => {
    if (stage === 'loading') loadConfig();
  }, [stage, loadConfig]);

  const handleReorder = useCallback((from, to) => {
    setItems((prev) => {
      if (from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }, []);

  const submit = useCallback(async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await api.createBucketNewsletter(
        bucketId,
        items.map((i) => i.bucketStoryId),
        manualOrderLocked,
        { silent: true },
      );
      onCreated(res.job);
    } catch (err) {
      setError(err?.response?.data?.error || 'The newsletter could not be started.');
      setSubmitting(false);
    }
  }, [bucketId, items, manualOrderLocked, onCreated]);

  const generateSetup = useCallback(async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await api.generateBucketNewsletterSetup(bucketId, description, { silent: true });
      setNameOptions(res.names || []);
      setName((res.names || [])[0] || '');
      setPrompt(res.reformattingPrompt || '');
      setStage('confirm');
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not generate newsletter names. Please try again.');
    } finally {
      setGenerating(false);
    }
  }, [bucketId, description]);

  const saveAndSubmit = useCallback(async () => {
    setSubmitting(true);
    setError('');
    try {
      // CFG-06 — the configuration is saved before the pending job launches.
      await api.saveBucketNewsletterConfig(bucketId, {
        name,
        description,
        reformattingPrompt: prompt,
        generatedNameOptions: nameOptions,
      }, { silent: true });
    } catch (err) {
      setError(err?.response?.data?.error || 'The newsletter settings could not be saved.');
      setSubmitting(false);
      return;
    }
    await submit();
  }, [bucketId, name, description, prompt, nameOptions, submit]);

  const errorBlock = error ? (
    <div className="pbnew-nl-error" role="alert">{error}</div>
  ) : null;

  if (stage === 'cap') {
    return (
      <AppModal
        isOpen
        title="Too many stories"
        onCancel={onCancel}
        onConfirm={() => setStage('loading')}
        confirmLabel={`Continue with first ${maxStories}`}
        maxWidth={480}
        modalTestId="newsletter-cap-modal"
      >
        <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E', lineHeight: 1.5 }}>
          Newsletter creation for more than {maxStories} stories is not possible.
        </p>
        <p style={{ margin: '10px 0 0', fontSize: 14, color: '#3C4A5E', lineHeight: 1.5 }}>
          You selected {maxStories + overflowCount}. Continuing will use the first{' '}
          <strong>{maxStories}</strong> stories in the order shown, and leave the
          remaining {overflowCount} out.
        </p>
      </AppModal>
    );
  }

  if (stage === 'loading') {
    return (
      <AppModal isOpen title="Create newsletter" onCancel={onCancel} showCancel={false} footer={<div />} maxWidth={460}>
        <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E' }}>Checking newsletter settings…</p>
      </AppModal>
    );
  }

  if (stage === 'order') {
    return (
      <AppModal
        isOpen
        title="Create newsletter"
        onCancel={onCancel}
        onConfirm={submit}
        confirmLabel="Create newsletter"
        isSubmitting={submitting}
        maxWidth={560}
        modalTestId="newsletter-order-modal"
      >
        <p style={{ margin: '0 0 4px', fontSize: 14, color: '#3C4A5E', lineHeight: 1.5 }}>
          {items.length} {items.length === 1 ? 'story' : 'stories'} from{' '}
          <strong>{bucketName}</strong>, in {targetLanguage || 'English'}.
        </p>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#3C4A5E', lineHeight: 1.5 }}>
          Drag to set the order. If you leave it as it is, the newsletter may be
          ordered for the strongest lead.
        </p>
        <NewsletterOrderList
          items={items}
          onReorder={handleReorder}
          onDragStart={() => setManualOrderLocked(true)}
        />
        {errorBlock}
      </AppModal>
    );
  }

  if (stage === 'describe') {
    return (
      <AppModal
        isOpen
        title="Set up this newsletter"
        onCancel={onCancel}
        onConfirm={generateSetup}
        confirmLabel={generating ? 'Generating…' : 'Continue'}
        confirmDisabled={!description.trim() || generating}
        isSubmitting={generating}
        maxWidth={560}
        modalTestId="newsletter-describe-modal"
      >
        <p style={{ margin: '0 0 12px', fontSize: 13, color: '#3C4A5E', lineHeight: 1.5 }}>
          This bucket does not have a newsletter yet. Your {items.length}{' '}
          selected {items.length === 1 ? 'story is' : 'stories are'} kept while
          you set one up.
        </p>
        <label className="pbnew-nl-label" htmlFor="pbnew-nl-description">
          Briefly describe the newsletter’s intended readers, style and tone.
        </label>
        <textarea
          id="pbnew-nl-description"
          className="pbnew-nl-textarea"
          rows={5}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. For busy regional editors. Direct, factual, no hype. Short items with a clear takeaway."
        />
        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#3C4A5E', lineHeight: 1.5 }}>
          You do not need to describe the stories themselves — the ones you
          selected already define this edition’s content.
        </p>
        {errorBlock}
      </AppModal>
    );
  }

  // stage === 'confirm'
  return (
    <AppModal
      isOpen
      title="Name and style"
      onCancel={onCancel}
      onConfirm={saveAndSubmit}
      confirmLabel="Save and create newsletter"
      confirmDisabled={!name.trim() || !prompt.trim() || submitting}
      isSubmitting={submitting}
      maxWidth={620}
      modalTestId="newsletter-confirm-modal"
    >
      <div className="pbnew-nl-section">
        <span className="pbnew-nl-label">Choose a name</span>
        {nameOptions.map((option) => (
          <label key={option} className="pbnew-nl-radio">
            <input
              type="radio"
              name="pbnew-nl-name"
              checked={name === option}
              onChange={() => setName(option)}
            />
            <span>{option}</span>
          </label>
        ))}
        <label className="pbnew-nl-label" htmlFor="pbnew-nl-name-input" style={{ marginTop: 10 }}>
          Or write your own
        </label>
        <input
          id="pbnew-nl-name-input"
          className="pbnew-nl-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="pbnew-nl-section">
        <label className="pbnew-nl-label" htmlFor="pbnew-nl-prompt">
          Style and tone
        </label>
        <textarea
          id="pbnew-nl-prompt"
          className="pbnew-nl-textarea"
          rows={9}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
        />
        <p style={{ margin: '8px 0 0', fontSize: 12, color: '#3C4A5E', lineHeight: 1.5 }}>
          This governs how every edition is written. You can edit it later from
          the bucket’s newsletter settings.
        </p>
      </div>
      {errorBlock}
    </AppModal>
  );
}
